import React from 'react';
import { Link } from 'react-router-dom';
import { LocationDescriptor } from 'history';
import styled from 'styled-components';
import { withAnalyticOnEvent } from 'react-shisell';
import { LinkId } from '../constants';

export type DynamicLinkProps = {
  id?: LinkId | string;
  to?: LocationDescriptor;
  onClick?: (event: React.MouseEvent) => void;
  className?: string;
  target?: string;
  showLeavingIcon?: boolean;
  analyticsExtras?: Record<string, string | undefined>;
};

const s = {
  Anchor: styled.a`
    cursor: pointer;
  `,
  LeavingIconSVG: styled.svg`
    fill: currentColor;
    vertical-align: middle;
    margin-right: 8px;
  `,
};

const LeavingIcon: React.FC = () => (
  <s.LeavingIconSVG width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M19 18.2393V12.2393C19 11.6873 19.447 11.2393 20 11.2393C20.553 11.2393 21 11.6873 21 12.2393V18.2393C21 19.8933 19.654 21.2393 18 21.2393H6C4.346 21.2393 3 19.8933 3 18.2393V6.23926C3 4.58526 4.346 3.23926 6 3.23926H12C12.553 3.23926 13 3.68726 13 4.23926C13 4.79126 12.553 5.23926 12 5.23926H6C5.448 5.23926 5 5.68826 5 6.23926V18.2393C5 18.7903 5.448 19.2393 6 19.2393H18C18.552 19.2393 19 18.7903 19 18.2393ZM17.5781 5.24726L15.9951 5.23926C15.4421 5.23626 14.9971 4.78626 15.0001 4.23426C15.0031 3.68326 15.4501 3.23926 16.0001 3.23926H16.0051L20.0021 3.25926C20.5521 3.26226 20.9971 3.70826 20.9971 4.25826L21.0001 8.23826C21.0001 8.79126 20.5531 9.23926 20.0011 9.23926H20.0001C19.4481 9.23926 19.0001 8.79226 19.0001 8.24026L18.9991 6.65426L12.7071 12.9463C12.5121 13.1413 12.2561 13.2393 12.0001 13.2393C11.7441 13.2393 11.4881 13.1413 11.2931 12.9463C10.9021 12.5553 10.9021 11.9233 11.2931 11.5323L17.5781 5.24726Z"
    />
    <mask id="mask0" maskUnits="userSpaceOnUse" x="3" y="3" width="18" height="19">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M19 18.2393V12.2393C19 11.6873 19.447 11.2393 20 11.2393C20.553 11.2393 21 11.6873 21 12.2393V18.2393C21 19.8933 19.654 21.2393 18 21.2393H6C4.346 21.2393 3 19.8933 3 18.2393V6.23926C3 4.58526 4.346 3.23926 6 3.23926H12C12.553 3.23926 13 3.68726 13 4.23926C13 4.79126 12.553 5.23926 12 5.23926H6C5.448 5.23926 5 5.68826 5 6.23926V18.2393C5 18.7903 5.448 19.2393 6 19.2393H18C18.552 19.2393 19 18.7903 19 18.2393ZM17.5781 5.24726L15.9951 5.23926C15.4421 5.23626 14.9971 4.78626 15.0001 4.23426C15.0031 3.68326 15.4501 3.23926 16.0001 3.23926H16.0051L20.0021 3.25926C20.5521 3.26226 20.9971 3.70826 20.9971 4.25826L21.0001 8.23826C21.0001 8.79126 20.5531 9.23926 20.0011 9.23926H20.0001C19.4481 9.23926 19.0001 8.79226 19.0001 8.24026L18.9991 6.65426L12.7071 12.9463C12.5121 13.1413 12.2561 13.2393 12.0001 13.2393C11.7441 13.2393 11.4881 13.1413 11.2931 12.9463C10.9021 12.5553 10.9021 11.9233 11.2931 11.5323L17.5781 5.24726Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0)">
      <rect y="0.239258" width="24" height="24" />
    </g>
  </s.LeavingIconSVG>
);

const externalUrlRegex = /^(https?:\/\/|\/\/|tel:)/;

const DynamicLink: React.SFC<DynamicLinkProps> = (props) => {
  const { to } = props;

  let url: string | undefined = undefined;
  if (to) {
    url = typeof to === 'string' ? to : `${to.pathname}${to.search}`;
  }

  const isNotClickable = !url && !props.onClick;
  const isExternal = url && externalUrlRegex.test(url);
  const baseProps = {
    id: props.id,
    onClick: props.onClick,
    target: props.target,
    className: props.className,
    children: props.children,
  };

  if (isNotClickable) {
    return <span className={props.className}>{props.children}</span>;
  }

  if (!to) {
    return <s.Anchor {...baseProps} />;
  }

  const leavingIcon = props.target === '_blank' && props.showLeavingIcon ? <LeavingIcon /> : <></>;

  return isExternal ? (
    <s.Anchor {...baseProps} href={url}>
      {leavingIcon}
      {props.children}
    </s.Anchor>
  ) : (
    <Link {...baseProps} to={to}>
      {leavingIcon}
      {props.children}
    </Link>
  );
};

const DynamicLinkWithAnalytics = withAnalyticOnEvent<DynamicLinkProps>({
  eventName: 'onClick',
  analyticName: 'SH_WEB_EVENT',
  extras: { ActionId: 'Click' },
})(DynamicLink);

const DynamicLinkWithAnalyticsWrapper: React.FC<DynamicLinkProps> = (props) => (
  <DynamicLinkWithAnalytics {...props} analyticsExtras={{ ElementId: props.id, ...props.analyticsExtras }} />
);

export default DynamicLinkWithAnalyticsWrapper;



// WEBPACK FOOTER //
// ./packages/smart-home-support/components/DynamicLink.tsx